import { useEffect, useState } from "react";
import { ContactRound, Search } from "lucide-react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Accounts() {
  const [accounts, setAccounts] = useState([]);
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const load = async () => {
    setLoading(true); setError(""); setAccounts([]);
    try { setAccounts((await api.get("/admin/accounts")).data); }
    catch { setError("Could not load accounts. Please try again."); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const needle = query.trim().toLowerCase();
  const visible = accounts.filter(({ name, email }) => name.toLowerCase().includes(needle) || email.includes(needle));
  return (
    <section className="max-w-4xl" data-testid="admin-accounts-page">
      <div className="flex items-center gap-3"><ContactRound className="w-8 h-8 text-[#B36906] shrink-0" /><h1 className="font-display font-black text-3xl" data-testid="admin-accounts-title">Accounts</h1></div>
      <p className="mt-3 text-sm text-[#7A7D8C]" data-testid="admin-accounts-description">Registered parent and Google account holders, combined by email. This read-only directory shows names and email addresses only.</p>
      <div className="mt-8 relative max-w-md">
        <label htmlFor="account-search" className="block text-sm font-semibold mb-2">Find an account</label>
        <Search className="absolute left-3 bottom-3 w-4 h-4 text-[#7A7D8C]" aria-hidden="true" />
        <Input id="account-search" data-testid="admin-accounts-search" type="search" placeholder="Search by name or email" value={query} onChange={(event) => setQuery(event.target.value)} className="pl-9 bg-white rounded-xl" />
      </div>
      {loading ? <p className="mt-6 text-sm" role="status" data-testid="admin-accounts-loading">Loading accounts…</p> : error ? (
        <div role="alert" className="mt-6 text-sm text-red-700" data-testid="admin-accounts-error">{error}<Button onClick={load} variant="outline" className="ml-3 rounded-full" data-testid="admin-accounts-retry">Retry</Button></div>
      ) : <>
        <p className="mt-6 text-sm text-[#7A7D8C]" aria-live="polite" data-testid="admin-accounts-count">{visible.length} of {accounts.length} accounts</p>
        <div className="mt-3 rounded-2xl border border-slate-100 bg-white overflow-x-auto">
          <table className="w-full text-sm table-fixed" data-testid="admin-accounts-table">
            <thead><tr className="text-left text-xs uppercase tracking-wider text-[#7A7D8C] border-b border-slate-100"><th scope="col" className="p-4 w-2/5">Name</th><th scope="col" className="p-4">Email</th></tr></thead>
            <tbody>
              {visible.map(({ name, email }) => <tr key={email} className="border-b last:border-0 border-slate-100 hover:bg-[#FFBF69]/10 transition-colors" data-testid={`admin-account-row-${email}`}>
                <td className="p-4 font-semibold break-words" data-testid={`admin-account-name-${email}`}>{name || "Name not provided"}</td>
                <td className="p-4 break-all" data-testid={`admin-account-email-${email}`}>{email}</td>
              </tr>)}
              {!visible.length && <tr><td colSpan={2} className="p-8 text-center text-[#7A7D8C]" data-testid="admin-accounts-empty">{accounts.length ? "No accounts match your search." : "No registered accounts yet."}</td></tr>}
            </tbody>
          </table>
        </div>
      </>}
    </section>
  );
}
